import React, { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { Button, Modal, ModalFooter } from "@telluscompany/design-system";
import { useLocalization } from "@i18n";
import { LoadingBlurDiv } from "@shared/components";
import {
  DEFAULT_PAGE_SIZE,
  EMPTY_PAGINATION_META,
  type PageSize,
} from "@shared/constants/pagination.constants";
import { useGeneralStructureRaisingHistoryQuery } from "../../api/generalRaisingHistory/generalRaisingHistory.queries";
import type {
  DamRaisingMethod,
  GeneralRaisingHistoryFilters,
} from "../../api/generalRaisingHistory/generalRaisingHistory.types";
import { formatGeneralDisplayValue } from "../../utils/formatGeneralDisplayValue";
import {
  mapRaisingHistorySortToApi,
  type GeneralDashboardTableSort,
} from "../../utils/generalDashboardTableSort";
import { GeneralDashboardPaginatedTable } from "../GeneralDashboardPaginatedTable";
import { GeneralRaisingHistoryFilter } from "../filters/GeneralRaisingHistoryFilter";

interface GeneralRaisingHistoryModalProps {
  open: boolean;
  onClose: () => void;
  structureId: string | null;
}

const RAISING_HISTORY_TABLE_ID = "general-dashboard-raising-history-table";

const METHOD_LABEL_KEYS: Record<DamRaisingMethod, string> = {
  downstream: "admin.structures.raisingHistory.method.downstream",
  upstream: "admin.structures.raisingHistory.method.upstream",
  centerLine: "admin.structures.raisingHistory.method.centerLine",
};

export const GeneralRaisingHistoryModal: React.FC<GeneralRaisingHistoryModalProps> = ({
  open,
  onClose,
  structureId,
}) => {
  const { translate } = useLocalization();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [appliedFilters, setAppliedFilters] = useState<
    Pick<GeneralRaisingHistoryFilters, "stage" | "method">
  >({});
  const [tableSort, setTableSort] = useState<GeneralDashboardTableSort | null>(
    null,
  );

  useEffect(() => {
    if (open) {
      setPage(1);
      setLimit(DEFAULT_PAGE_SIZE);
      setAppliedFilters({});
      setTableSort(null);
    }
  }, [open, structureId]);

  const queryFilters = useMemo(
    () => ({
      ...appliedFilters,
      page,
      limit,
      ...mapRaisingHistorySortToApi(tableSort),
    }),
    [appliedFilters, limit, page, tableSort],
  );

  const { data, isFetching, isError } = useGeneralStructureRaisingHistoryQuery(
    open && structureId ? structureId : undefined,
    queryFilters,
  );

  const handleFilter = useCallback(
    (filters: Pick<GeneralRaisingHistoryFilters, "stage" | "method">) => {
      setAppliedFilters(filters);
      setPage(1);
    },
    [],
  );

  const handleClearFilter = useCallback(() => {
    setAppliedFilters({});
    setPage(1);
  }, []);

  const handleSortChange = useCallback<
    Dispatch<SetStateAction<GeneralDashboardTableSort | null>>
  >((value) => {
    setTableSort((current) => {
      const next =
        typeof value === "function"
          ? value(current ?? { key: "", direction: "asc" })
          : value;
      return next;
    });
    setPage(1);
  }, []);

  const tableFilter = useCallback(
    () => (
      <GeneralRaisingHistoryFilter
        onFilter={handleFilter}
        onClear={handleClearFilter}
        initialFilters={{
          stage: appliedFilters.stage,
          method: appliedFilters.method,
        }}
      />
    ),
    [appliedFilters.method, appliedFilters.stage, handleClearFilter, handleFilter],
  );

  const tableColumns = useMemo(
    () => [
      {
        key: "stage",
        label: translate("admin.structures.raisingHistory.label.stage"),
        minWidth: "100px",
        sortType: "text" as const,
      },
      {
        key: "methodDisplay",
        label: translate("admin.structures.raisingHistory.label.method"),
        minWidth: "140px",
        sortType: "text" as const,
      },
      {
        key: "elevation",
        label: translate("admin.structures.raisingHistory.label.elevationShort"),
        minWidth: "120px",
        sortType: "number" as const,
      },
      {
        key: "occurredOnDisplay",
        label: translate("admin.structures.raisingHistory.label.occurredOn"),
        minWidth: "120px",
        sortType: "date" as const,
      },
      {
        key: "executiveProjectDesigner",
        label: translate(
          "admin.structures.raisingHistory.label.executiveProjectDesigner",
        ),
        minWidth: "220px",
      },
    ],
    [translate],
  );

  const tableData = useMemo(
    () =>
      (data?.data ?? []).map((item) => {
        const methodKey = METHOD_LABEL_KEYS[item.method];
        const translatedMethod = methodKey ? translate(methodKey) : item.method;

        return {
          ...item,
          stage: formatGeneralDisplayValue(item.stage),
          methodDisplay:
            methodKey && translatedMethod !== methodKey
              ? translatedMethod
              : formatGeneralDisplayValue(item.method),
          elevation: formatGeneralDisplayValue(item.elevation),
          occurredOnDisplay: formatGeneralDisplayValue(item.occurredOn, true),
          executiveProjectDesigner: formatGeneralDisplayValue(
            item.executiveProjectDesigner,
          ),
        };
      }),
    [data?.data, translate],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={translate("general.dashboard.raisingHistory.modalTitle")}
      content={
        <LoadingBlurDiv isLoading={isFetching}>
          <div className="general-dashboard-modal-body general-dashboard-modal-body--table">
            {isError && (
              <p className="text-sm text-destructive">
                {translate("general.dashboard.loadError")}
              </p>
            )}

            <GeneralDashboardPaginatedTable
              tableId={RAISING_HISTORY_TABLE_ID}
              tableColumns={tableColumns}
              tableData={tableData}
              meta={data?.meta ?? EMPTY_PAGINATION_META}
              isLoading={isFetching}
              onPageChange={setPage}
              onLimitChange={(nextLimit) => {
                setLimit(nextLimit);
                setPage(1);
              }}
              onSortChange={handleSortChange}
              tableFilter={tableFilter}
              filterButtonText={translate(
                "admin.structures.raisingHistory.table.filterButtonText",
              )}
            />
          </div>
          <ModalFooter
            footer={
              <Button type="button" variant="primary" onClick={onClose}>
                {translate("general.dashboard.button.close")}
              </Button>
            }
          />
        </LoadingBlurDiv>
      }
    />
  );
};
